/**
 * SimpleTaxonomySelectionV3
 *
 * A completely new implementation of the taxonomy selection component that uses
 * the enhanced taxonomy service for improved reliability and error handling.
 *
 * Key features:
 * - Multi-tiered fallback system for subcategory loading
 * - Enhanced error handling and debug information
 * - Card-based grid layout for better visual representation
 * - Better state management for layer/category/subcategory selection
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  isProduction,
  isDevelopment,
  environmentSafeLog,
} from '../../utils/environment';
import { enhancedTaxonomyService } from '../../services/enhancedTaxonomyService';
import { FALLBACK_SUBCATEGORIES } from '../../services/taxonomyFallbackData';
import { taxonomyService } from '../../services/simpleTaxonomyService';
import { TaxonomyItem } from '../../types/taxonomy.types';
import { logger } from '../../utils/logger';
import '../../styles/SimpleTaxonomySelection.css';

// Layer display names for better UX
const LAYER_NAMES: Record<string, string> = {
  G: 'Generics',
  S: 'Stars',
  L: 'Looks',
  M: 'Moves',
  W: 'Worlds',
  B: 'Beats',
  P: 'Places',
  T: 'Things',
  C: 'Concepts',
  R: 'Realms',
};

interface SimpleTaxonomySelectionV3Props {
  selectedLayer: string;
  onLayerSelect: (layer: string) => void;
  selectedCategoryCode: string;
  onCategorySelect: (category: string) => void;
  selectedSubcategoryCode: string;
  onSubcategorySelect: (subcategory: string) => void;
}

const SimpleTaxonomySelectionV3: React.FC<SimpleTaxonomySelectionV3Props> = ({
  selectedLayer,
  onLayerSelect,
  selectedCategoryCode,
  onCategorySelect,
  selectedSubcategoryCode,
  onSubcategorySelect,
}) => {
  // Use the Async Taxonomy Sync Protocol
  // State for UI control
  const [categoryOptions, setCategoryOptions] = useState<TaxonomyItem[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<TaxonomyItem[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [debugMode, setDebugMode] = useState<boolean>(false);

  // References for tracking loading attempts
  const loadAttemptsRef = useRef<Record<string, number>>({});
  const subcategoryCacheRef = useRef<Record<string, TaxonomyItem[]>>({});
  const isMounted = useRef(true);
  const previousLayerRef = useRef<string>('');

  // Helper to get a cache key for subcategory lookups
  const getSubcategoryCacheKey = useCallback(
    (layer: string, categoryCode: string): string => {
      return `${layer}.${categoryCode}`;
    },
    []
  );

  // Helper function to fetch categories from backend API
  const fetchCategories = useCallback(
    async (layer: string): Promise<TaxonomyItem[]> => {
      try {
        console.log(
          `🚀 [SimpleTaxonomySelectionV3] Fetching categories for layer: ${layer} using enhancedTaxonomyService`
        );
        const categories = await enhancedTaxonomyService.getCategories(layer);
        console.log(
          `✅ [SimpleTaxonomySelectionV3] Loaded ${categories.length} categories for layer ${layer}:`,
          categories
        );
        return categories;
      } catch (error) {
        console.error(
          `❌ [SimpleTaxonomySelectionV3] Failed to fetch categories for layer ${layer}:`,
          error
        );
        throw error;
      }
    },
    []
  );

  // Helper function to fetch subcategories using enhanced taxonomy service
  const fetchSubcategories = useCallback(
    async (layer: string, categoryCode: string): Promise<TaxonomyItem[]> => {
      try {
        console.log(
          `🚀 [SimpleTaxonomySelectionV3] Fetching subcategories for ${layer}.${categoryCode} using enhancedTaxonomyService`
        );
        const subcategories = await enhancedTaxonomyService.getSubcategories(
          layer,
          categoryCode
        );
        console.log(
          `✅ [SimpleTaxonomySelectionV3] Loaded ${subcategories.length} subcategories for ${layer}.${categoryCode}:`,
          subcategories
        );
        return subcategories;
      } catch (error) {
        console.error(
          `❌ [SimpleTaxonomySelectionV3] Failed to fetch subcategories for ${layer}.${categoryCode}:`,
          error
        );
        throw error;
      }
    },
    []
  );

  // Clean up on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Component initialization - no longer using sync protocol

  // Load categories when layer changes using Backend Taxonomy API
  useEffect(() => {
    if (selectedLayer) {
      setIsProcessing(true);
      setError(null);

      const loadCategories = async () => {
        try {
          logger.info(
            `🚀 [TAXONOMY SYNC] Loading categories for layer: ${selectedLayer} using Backend API`
          );

          const categories = await fetchCategories(selectedLayer);

          if (isMounted.current) {
            setCategoryOptions(categories);
            logger.info(
              `✅ [TAXONOMY SYNC] Loaded ${categories.length} categories for layer ${selectedLayer}`
            );

            // Clear subcategory only if layer actually changed
            if (previousLayerRef.current !== selectedLayer && selectedSubcategoryCode) {
              logger.info(`🔄 [TAXONOMY SYNC] Layer changed from ${previousLayerRef.current} to ${selectedLayer}, clearing subcategory`);
              onSubcategorySelect('');
            }

            // Update previous layer reference
            previousLayerRef.current = selectedLayer;
          }
        } catch (err) {
          logger.error(
            `❌ [TAXONOMY SYNC] Error loading categories for ${selectedLayer}:`,
            err
          );
          if (isMounted.current) {
            setError(
              `Failed to load categories for ${selectedLayer}: ${
                err instanceof Error ? err.message : 'Unknown error'
              }`
            );

            // Fallback to frontend service on error
            logger.info(
              '🔧 [TAXONOMY SYNC] Falling back to frontend taxonomy service'
            );
            try {
              const fallbackCategories =
                taxonomyService.getCategories(selectedLayer);
              setCategoryOptions(fallbackCategories);
            } catch (fallbackErr) {
              logger.error(
                '❌ [TAXONOMY SYNC] Fallback also failed:',
                fallbackErr
              );
              setCategoryOptions([]);
            }
          }
        } finally {
          if (isMounted.current) {
            setIsProcessing(false);
          }
        }
      };

      loadCategories();
    } else {
      setCategoryOptions([]);
    }
  }, [
    selectedLayer,
    fetchCategories,
  ]);

  // Load subcategories when layer and category change using Backend Taxonomy API
  useEffect(() => {
    if (!selectedLayer || !selectedCategoryCode) {
      setSubcategoryOptions([]);
      return;
    }

    const loadSubcategories = async () => {
      setIsProcessing(true);
      setError(null);

      const cacheKey = getSubcategoryCacheKey(
        selectedLayer,
        selectedCategoryCode
      );

      try {
        // Check cache first
        if (
          subcategoryCacheRef.current[cacheKey] &&
          subcategoryCacheRef.current[cacheKey].length > 0
        ) {
          logger.info(
            `🗄️ [TAXONOMY SYNC] Using cached subcategories for ${cacheKey}`
          );
          if (isMounted.current) {
            setSubcategoryOptions(subcategoryCacheRef.current[cacheKey]);
          }
          return;
        }

        // Fetch from Backend Taxonomy API
        logger.info(
          `🚀 [TAXONOMY SYNC] Loading subcategories for ${cacheKey} using Backend API`
        );
        const subcategories = await fetchSubcategories(
          selectedLayer,
          selectedCategoryCode
        );

        if (subcategories && subcategories.length > 0) {
          logger.info(
            `✅ [TAXONOMY SYNC] Loaded ${subcategories.length} subcategories for ${cacheKey}`
          );
          if (isMounted.current) {
            setSubcategoryOptions(subcategories);
            subcategoryCacheRef.current[cacheKey] = subcategories;
          }
          return;
        }

        // If no subcategories found, try fallback data
        logger.info(
          `🔧 [TAXONOMY SYNC] No subcategories from API, trying fallback data for ${cacheKey}`
        );
        if (FALLBACK_SUBCATEGORIES[selectedLayer]?.[selectedCategoryCode]) {
          const fallbackData =
            FALLBACK_SUBCATEGORIES[selectedLayer][selectedCategoryCode];
          if (isMounted.current) {
            setSubcategoryOptions(fallbackData);
            subcategoryCacheRef.current[cacheKey] = fallbackData;
          }
          return;
        }

        // Last resort: try frontend taxonomy service
        logger.info(
          `🔧 [TAXONOMY SYNC] Trying frontend taxonomy service fallback for ${cacheKey}`
        );
        const frontendSubcategories = taxonomyService.getSubcategories(
          selectedLayer,
          selectedCategoryCode
        );

        if (frontendSubcategories && frontendSubcategories.length > 0) {
          if (isMounted.current) {
            setSubcategoryOptions(frontendSubcategories);
            subcategoryCacheRef.current[cacheKey] = frontendSubcategories;
          }
        } else {
          // Emergency fallback
          const emergencyOptions: TaxonomyItem[] = [
            { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
          ];

          logger.warn(
            `⚠️ [TAXONOMY SYNC] Using emergency fallback for ${cacheKey}`
          );
          if (isMounted.current) {
            setSubcategoryOptions(emergencyOptions);
            subcategoryCacheRef.current[cacheKey] = emergencyOptions;
          }
        }
      } catch (err) {
        logger.error(
          `❌ [TAXONOMY SYNC] Error loading subcategories for ${cacheKey}:`,
          err
        );
        if (isMounted.current) {
          setError(
            `Failed to load subcategories for ${selectedCategoryCode}: ${
              err instanceof Error ? err.message : 'Unknown error'
            }`
          );

          // Emergency fallback on error
          const emergencyOptions: TaxonomyItem[] = [
            { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
          ];

          setSubcategoryOptions(emergencyOptions);
          subcategoryCacheRef.current[cacheKey] = emergencyOptions;
        }
      } finally {
        if (isMounted.current) {
          setIsProcessing(false);
        }
      }
    };

    loadSubcategories();
  }, [
    selectedLayer,
    selectedCategoryCode,
    getSubcategoryCacheKey,
    fetchSubcategories,
  ]);

  useEffect(() => {
    if (!selectedCategoryCode && selectedSubcategoryCode) {
      onSubcategorySelect('');
    }
  }, [selectedCategoryCode, selectedSubcategoryCode, onSubcategorySelect]);

  // Get subcategory code from full code (e.g., "POP.KPO" -> "KPO")
  const getShortSubcategoryCode = (fullCode: string): string => {
    if (!fullCode) return '';

    // Handle both formats: "POP.KPO" and "KPO"
    if (fullCode.includes('.')) {
      const parts = fullCode.split('.');
      // If we have format like "POP.KPO", return "KPO"
      return parts.length > 1 ? parts[1] : parts[0];
    }

    // If there's no dot, return the code as is
    return fullCode;
  };

  // Handle retry for subcategory loading
  const handleRetrySubcategories = () => {
    if (!selectedLayer || !selectedCategoryCode) return;

    const cacheKey = getSubcategoryCacheKey(
      selectedLayer,
      selectedCategoryCode
    );
    delete subcategoryCacheRef.current[cacheKey];
    setSubcategoryOptions([]);

    // Force re-loading
    setIsProcessing(true);
    setTimeout(async () => {
      try {
        // Try to get subcategories from the Backend Taxonomy API
        const subcategories = await fetchSubcategories(
          selectedLayer,
          selectedCategoryCode
        );

        if (subcategories && subcategories.length > 0 && isMounted.current) {
          setSubcategoryOptions(subcategories);
          subcategoryCacheRef.current[cacheKey] = subcategories;
        } else {
          // If no subcategories found, try emergency fallback
          logger.info(
            `No subcategories found for ${cacheKey}, trying emergency fallback`
          );

          if (!isMounted.current) return;

          // Emergency hardcoded fallbacks for known problematic combinations
          let emergencyOptions: TaxonomyItem[] = [];

          if (selectedLayer === 'S' && selectedCategoryCode === 'DNC') {
            logger.info(`Applying emergency fallback for S.DNC`);
            emergencyOptions = [
              { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
              { id: 2, code: 'PRD', numericCode: '002', name: 'Producer' },
              { id: 3, code: 'HSE', numericCode: '003', name: 'House' },
              { id: 4, code: 'TEC', numericCode: '004', name: 'Techno' },
              { id: 5, code: 'TRN', numericCode: '005', name: 'Trance' },
            ];
          } else if (selectedLayer === 'L' && selectedCategoryCode === 'PRF') {
            logger.info(`Applying emergency fallback for L.PRF`);
            emergencyOptions = [
              { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
              { id: 2, code: 'LEO', numericCode: '002', name: 'Leotard' },
              { id: 3, code: 'SEQ', numericCode: '003', name: 'Sequined' },
              { id: 4, code: 'LED', numericCode: '004', name: 'LED' },
            ];
          } else {
            // Default synthetic entry if no specific emergency fallback is available
            emergencyOptions = [
              { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
            ];
          }

          logger.info(
            `Applied emergency fallback with ${emergencyOptions.length} options`
          );
          setSubcategoryOptions(emergencyOptions);
          subcategoryCacheRef.current[cacheKey] = emergencyOptions;
        }
      } catch (error) {
        logger.error(`Error during retry for ${cacheKey}:`, error);

        if (!isMounted.current) return;

        // Use basic fallback on error
        const basicFallback: TaxonomyItem[] = [
          { id: 1, code: 'BAS', numericCode: '001', name: 'Base' },
        ];

        setSubcategoryOptions(basicFallback);
        subcategoryCacheRef.current[cacheKey] = basicFallback;
      } finally {
        if (isMounted.current) {
          setIsProcessing(false);
        }
      }
    }, 100);
  };

  // Toggle debug mode - only functional in development environment
  const toggleDebugMode = () => {
    // Don't allow toggling in production
    if (isProduction()) {
      return;
    }

    const newMode = !debugMode;
    setDebugMode(newMode);

    // Store debug mode preference in sessionStorage
    try {
      if (typeof window !== 'undefined' && sessionStorage) {
        sessionStorage.setItem('taxonomyDebugMode', newMode.toString());
      }
    } catch (e) {
      environmentSafeLog(
        'Could not store debug mode preference in sessionStorage'
      );
    }
  };

  // Initialize debug mode from query params or sessionStorage
  useEffect(() => {
    try {
      // Always disable debug mode in production environment
      if (isProduction()) {
        setDebugMode(false);
        // Clear any stored debug mode preference in production
        if (typeof window !== 'undefined' && sessionStorage) {
          sessionStorage.removeItem('taxonomyDebugMode');
        }
        return;
      }

      // In development, check for debug parameters
      const hasDebugParam =
        typeof window !== 'undefined' &&
        (window.location.search.includes('debug=true') ||
          window.location.search.includes('debug_mode=true'));

      // Check for stored debug mode preference
      let storedDebugMode = false;
      try {
        if (typeof window !== 'undefined' && sessionStorage) {
          storedDebugMode =
            sessionStorage.getItem('taxonomyDebugMode') === 'true';
        }
      } catch (e) {
        // Silently fail if sessionStorage is not available
      }

      // Log the debug mode conditions for troubleshooting (only in development)
      environmentSafeLog('[DEBUG PANEL] Environment checks:', {
        isDevelopment: isDevelopment(),
        hasDebugParam,
        storedDebugMode,
        'process.env.NODE_ENV': process.env.NODE_ENV,
        'window.location.search': window.location.search,
      });

      // Only enable debug mode in development and if explicitly requested
      if (isDevelopment() && (hasDebugParam || storedDebugMode)) {
        setDebugMode(true);
        environmentSafeLog('[DEBUG PANEL] Debug mode enabled');
      } else {
        setDebugMode(false);
        environmentSafeLog('[DEBUG PANEL] Debug mode disabled');
      }
    } catch (error) {
      environmentSafeLog('[DEBUG PANEL] Error initializing debug mode:', error);
      setDebugMode(false);
    }
  }, []);

  // Format and display a TaxonomyItem
  const renderTaxonomyCard = (
    item: TaxonomyItem | string,
    isActive: boolean,
    onClick: () => void,
    type: 'layer' | 'category' | 'subcategory'
  ) => {
    // For layer type, item is a string
    if (type === 'layer') {
      const layerCode = item as string;
      const layerName = LAYER_NAMES[layerCode] || layerCode;

      return (
        <Card
          className={`taxonomy-item ${isActive ? 'active' : ''}`}
          elevation={isActive ? 3 : 1}
          onClick={onClick}
          sx={{
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            bgcolor: isActive ? '#e8f4ff' : '#fff',
            borderColor: isActive ? '#007bff' : '#ddd',
            border: isActive ? '2px solid #007bff' : '1px solid #ddd',
            height: '100%',
            minHeight: '110px',
            maxHeight: '110px',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3,
              borderColor: '#aaa',
              bgcolor: isActive ? '#e8f4ff' : '#f8f9fa',
            },
          }}
        >
          <CardActionArea>
            <CardContent>
              <Typography
                variant="h6"
                component="div"
                align="center"
                sx={{ fontWeight: 'bold' }}
              >
                {layerCode}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 0.5 }}
              >
                {layerName}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    }

    // For category and subcategory types, item is a TaxonomyItem
    const taxonomyItem = item as TaxonomyItem;
    const displayName = taxonomyItem.name || taxonomyItem.code;

    return (
      <Card
        className={`taxonomy-item ${isActive ? 'active' : ''}`}
        elevation={isActive ? 3 : 1}
        onClick={onClick}
        sx={{
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          bgcolor: isActive ? '#e8f4ff' : '#fff',
          borderColor: isActive ? '#007bff' : '#ddd',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3,
            borderColor: '#aaa',
            bgcolor: isActive ? '#e8f4ff' : '#f8f9fa',
          },
        }}
      >
        <CardActionArea>
          <CardContent>
            <Typography
              variant="body1"
              component="div"
              fontWeight="bold"
              noWrap
              sx={{ mb: 0.5, fontSize: '0.95rem' }}
            >
              {taxonomyItem.code}
            </Typography>
            <Tooltip
              title={displayName.replace(/_/g, ' ')}
              placement="top"
              arrow
              enterDelay={300}
              enterNextDelay={300}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  height: '40px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.2,
                  fontSize: '0.875rem',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  paddingLeft: '2px',
                  paddingRight: '2px',
                }}
              >
                {displayName.replace(/_/g, ' ')}
              </Typography>
            </Tooltip>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              {taxonomyItem.numericCode}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <div className="simple-taxonomy-selection">
      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetrySubcategories}
            >
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* We're keeping the layer selection in RegisterAssetPage, so we no longer need it here */}

      <Box mb={3} sx={{ opacity: selectedLayer ? 1 : 0.5 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 'bold', color: '#333' }}
        >
          Step 2.1: Select Category
        </Typography>
        <div className="taxonomy-section">
          <div className="taxonomy-section-title">
            <span>Category</span>
            {selectedCategoryCode && (
              <span className="category-indicator">{selectedCategoryCode}</span>
            )}
          </div>
          <div
            className={`taxonomy-items fixed-grid ${
              isProcessing ? 'loading' : ''
            }`}
          >
            {isProcessing && categoryOptions.length === 0 ? (
              <div className="taxonomy-loading">Loading categories...</div>
            ) : categoryOptions.length > 0 ? (
              categoryOptions.map(category => (
                <div key={category.code} className="taxonomy-item-wrapper">
                  {renderTaxonomyCard(
                    category,
                    category.code === selectedCategoryCode,
                    () => {
                      const nextCategoryCode = category.code;
                      if (nextCategoryCode !== selectedCategoryCode) {
                        onSubcategorySelect('');
                      }
                      onCategorySelect(nextCategoryCode);
                    },
                    'category'
                  )}
                </div>
              ))
            ) : selectedLayer ? (
              <div className="taxonomy-empty">
                No categories found
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setCategoryOptions([]);
                    setTimeout(async () => {
                      const categories = await fetchCategories(selectedLayer);
                      setCategoryOptions(categories);
                    }, 100);
                  }}
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="taxonomy-empty">Please select a layer first</div>
            )}
          </div>
        </div>
      </Box>

      <Box mb={3} sx={{ opacity: selectedCategoryCode ? 1 : 0.5 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: 'bold', color: '#333' }}
        >
          Step 2.2: Select Subcategory
        </Typography>
        <div className="taxonomy-section">
          <div className="taxonomy-section-title">
            <span>Subcategory</span>
            {selectedSubcategoryCode && (
              <span className="category-indicator">
                {selectedSubcategoryCode}
              </span>
            )}
          </div>
          <div
            className={`taxonomy-items fixed-grid ${
              isProcessing ? 'loading' : ''
            }`}
          >
            {isProcessing && subcategoryOptions.length === 0 ? (
              <div className="taxonomy-loading">Loading subcategories...</div>
            ) : subcategoryOptions.length > 0 ? (
              subcategoryOptions.map(subcategory => {
                // Handle both formats: "PRF.BAS" and "BAS"
                const displayCode = subcategory.code.includes('.')
                  ? subcategory.code.split('.')[1]
                  : subcategory.code;

                return (
                  <div key={displayCode} className="taxonomy-item-wrapper">
                    {renderTaxonomyCard(
                      subcategory,
                      displayCode === selectedSubcategoryCode,
                      () => {
                        if (!selectedCategoryCode) return;
                        setTimeout(() => {
                          onSubcategorySelect(displayCode);
                        }, 0);
                      },
                      'subcategory'
                    )}
                  </div>
                );
              })
            ) : selectedCategoryCode ? (
              <div className="taxonomy-empty">
                No subcategories found
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRetrySubcategories}
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="taxonomy-empty">
                Please select a category first
              </div>
            )}
          </div>
        </div>
      </Box>

      {/* Moving selection to the bottom navigation row - the chip will be centered */}
      {process.env.NODE_ENV !== 'production' &&
        window.location.search.includes('debug=true') && (
          <Box
            mb={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Button size="small" variant="outlined" onClick={toggleDebugMode}>
              {debugMode ? 'Hide Debug Info' : 'Show Debug Info'}
            </Button>
            {isProcessing && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>
        )}

      {/* Debug info completely hidden in production */}
      {debugMode &&
        process.env.NODE_ENV !== 'production' &&
        window.location.search.includes('debug=true') && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Debug Information
            </Typography>

            <Typography variant="body2">
              <strong>Layer:</strong> {selectedLayer || 'None'}
              <br />
              <strong>Category:</strong> {selectedCategoryCode || 'None'}
              <br />
              <strong>Subcategory:</strong> {selectedSubcategoryCode || 'None'}
              <br />
              <strong>Available Subcategories:</strong>{' '}
              {subcategoryOptions.length}
              <br />
              <strong>Load Attempts:</strong>{' '}
              {selectedLayer && selectedCategoryCode
                ? loadAttemptsRef.current[
                    getSubcategoryCacheKey(selectedLayer, selectedCategoryCode)
                  ] || 0
                : 0}
            </Typography>
          </Alert>
        )}

      {/* Navigation footer with centered selection chip */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 3,
          px: 1,
          py: 2,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Button variant="outlined" sx={{ visibility: 'hidden' }}>
          Back
        </Button>

        <Box sx={{ flexGrow: 0, mx: 'auto' }}>
          {selectedLayer && selectedCategoryCode && selectedSubcategoryCode ? (
            <Chip
              label={`Selected: ${selectedLayer}.${selectedCategoryCode}.${getShortSubcategoryCode(
                selectedSubcategoryCode
              )}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Please select a layer, category, and subcategory
            </Typography>
          )}
        </Box>

        <Button variant="outlined" sx={{ visibility: 'hidden' }}>
          Next
        </Button>
      </Box>
    </div>
  );
};

export default SimpleTaxonomySelectionV3;
